import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import {
  Box, Typography, Paper, TextField, FormControl,
  Select, Switch,
  MenuItem, Divider, CircularProgress, Alert,
  useTheme, Tooltip, Button,
  Chip, Stack, alpha,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { fetchRequest as fetchBlueprints } from '../../redux/actions/blueprints/list';
import { fetchRequest as fetchSegments } from '../../redux/actions/segments/list';
import PipelinePreview from '../common/PipelinePreview';
import { getModelDetail } from '../../redux/actions/models/detail';
import { fetchRequest as fetchBalance } from '../../redux/actions/billing/balance';
import { PipelinesIcon, ModelsIcon, SegmentsIcon } from '../global/CustomIcons';
import BoltIcon from '@mui/icons-material/Bolt';
import InfoIcon from '@mui/icons-material/Info';
import InsightsIcon from '@mui/icons-material/Insights';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import { editModelRequest, clearState } from '../../redux/actions/models/edit';
import PageBreadcrumbs from '../common/PageBreadcrumbs';
import TrainingScheduleField from './TrainingScheduleField';
import TrainingCostEstimator from './TrainingCostEstimator';

const daysOfWeek = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
];

// Pacing presets for the new-data trigger: the minimum gap between
// data-triggered runs (``retrain_triggers.on_new_data.cooldown_seconds``
// on the backend). This is what keeps a continuous stream — rows landing
// every second — from retraining back-to-back all day.
const RETRAIN_PACING_OPTIONS = [
  { value: 900, label: 'Every 15 min' },
  { value: 1800, label: 'Every 30 min' },
  { value: 3600, label: 'Every hour' },
  { value: 10800, label: 'Every 3 hours' },
  { value: 21600, label: 'Every 6 hours' },
  { value: 86400, label: 'Once a day' },
];
const DEFAULT_RETRAIN_PACING = 1800;

// Help text for fields — kept in sync with CreateModelPage so the two
// surfaces explain everything identically.
const helpTexts = {
  blueprint: "Pick one or more models. They form a pool: every run trains all of them on the same segment data, and the best result becomes the promotion candidate. Each extra model adds one full training's cost per run.",
  segment: "The segment contains the data that will be used to train the model.",
  forecast_horizon: "Number of future points to forecast ahead of the current time.",
  training_schedule: "Defines when the model will automatically train.",
  training_mode: "What triggers a training run: no automatic runs (manual only), a recurring schedule, or the arrival of new data in the segment.",
  promotion_policy: "What happens after a run's best training beats the version that's serving: promote it automatically (retiring the old one), ask you first, or do nothing.",
  budget: "Optional monthly credit cap for this pipeline's trainings. When reached, scheduled and data-triggered runs pause until next month — manual runs still work.",
};

// Plain, compact text field matching the create-pipeline surface.
const CMDarkTextField = ({ label, ...props }) => (
  <TextField variant="outlined" label={label} fullWidth size="small" {...props} />
);

// Field label with help icon
const FieldLabel = ({ label, helpText }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
    <Typography variant="body2" fontWeight={500}>{label}</Typography>
    {helpText && (
      <Tooltip title={helpText} arrow placement="top">
        <InfoIcon
          fontSize="small"
          sx={{ ml: 1, color: 'text.secondary', opacity: 0.7, fontSize: '0.9rem', cursor: 'help' }}
        />
      </Tooltip>
    )}
  </Box>
);

const EditModelPage = ({
  fetchBlueprints,
  fetchSegments,
  getModelDetail,
  blueprints,
  segments,
  modelDetail,
  modelDetailLoading,
  blueprintsLoading,
  segmentsLoading,
  modelEditing,
  modelEditResponse,
  modelEditError,
  loadBalance,
  balance,
}) => {
  const dispatch = useDispatch();
  const { modelId } = useParams();
  const theme = useTheme();

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [workspaceId, setWorkspaceId] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);

  // Form values
  const [formValues, setFormValues] = useState({
    name: '',
    blueprint: '',
    segment: '',
    training_schedule: {
      start_time: '00:00:00',
      end_time: '23:59:59',
      days: [...daysOfWeek],
      frequency: '86400'  // sane default: train once a day
    },
    forecast_horizon: 1,
    active: true,
    // Automation settings (flattened for form handling; assembled
    // into the API shape in handleSubmit).
    promotion_mode: 'manual',
    training_mode: 'once',               // 'once' | 'schedule' | 'data'
    retrain_min_rows: 100,
    retrain_cooldown: DEFAULT_RETRAIN_PACING,
    monthly_budget: '',
    challenger_blueprints: [],
  });

  // Get workspace ID from header/localStorage
  useEffect(() => {
    const id = localStorage.getItem('WORKSPACE_ID');
    if (id) {
      setWorkspaceId(id);
      setFormValues(prev => ({
        ...prev,
        workspace_id: id
      }));
    }
  }, []);

  // Fetch model details, blueprints and segments on component mount
  useEffect(() => {
    if (workspaceId && modelId) {
      getModelDetail(modelId);
      fetchBlueprints({ workspace_id: workspaceId });
      fetchSegments({ workspace_id: workspaceId });
    }
  }, [fetchBlueprints, fetchSegments, getModelDetail, workspaceId, modelId]);

  // Balance feeds the cost estimator's "your balance covers ~N months"
  // footer, same as the create page.
  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  // Populate form with model details once loaded
  useEffect(() => {
    if (modelDetail && !isLoaded) {
      setFormValues({
        ...formValues,
        name: modelDetail.name || '',
        blueprint: modelDetail.blueprint || '',
        segment: modelDetail.segment || '',
        training_schedule: modelDetail.training_schedule ? {
          start_time: modelDetail.training_schedule.start_time || '00:00:00',
          end_time: modelDetail.training_schedule.end_time || '23:59:59',
          days: modelDetail.training_schedule.days || [...daysOfWeek],
          frequency: modelDetail.training_schedule.frequency || '86400'
        } : formValues.training_schedule,
        forecast_horizon: modelDetail.forecast_horizon || 1,
        active: modelDetail.active !== undefined ? modelDetail.active : true,
        promotion_mode: modelDetail.promotion_policy?.mode || 'manual',
        // Trigger mode is derived: an enabled data trigger wins, then a
        // recurring schedule (non-empty days), else run-once / manual.
        training_mode: modelDetail.retrain_triggers?.on_new_data?.enabled
          ? 'data'
          : ((modelDetail.training_schedule?.days || []).length ? 'schedule' : 'once'),
        retrain_min_rows: modelDetail.retrain_triggers?.on_new_data?.min_rows || 100,
        retrain_cooldown: modelDetail.retrain_triggers?.on_new_data?.cooldown_seconds
          || DEFAULT_RETRAIN_PACING,
        monthly_budget: modelDetail.budget?.monthly_credit_cap || '',
        challenger_blueprints: modelDetail.challenger_blueprints || [],
        workspace_id: workspaceId
      });
      setIsLoaded(true);
    }
  }, [modelDetail, formValues, isLoaded, workspaceId]);

  // State to track form submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = useCallback((e) => {
    const { name, value, checked } = e.target;

    if (name === 'active') {
      setFormValues(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === 'forecast_horizon') {
      setFormValues(prev => ({
        ...prev,
        [name]: parseInt(value, 10) || 0
      }));
    } else {
      setFormValues(prev => ({
        ...prev,
        [name]: value
      }));
    }
  }, []);

  // Update the submit handler to use Redux action for editing
  const handleSubmit = useCallback(() => {
    setError(null);
    setIsSubmitting(true);

    try {
      // Assemble the API payload — the flattened automation form
      // fields are folded into their API shapes and excluded from
      // the top-level body.
      const {
        promotion_mode, training_mode, retrain_min_rows,
        retrain_cooldown, monthly_budget, challenger_blueprints,
        blueprint, ...base
      } = formValues;
      const payload = {
        ...base,
        // Non-schedule modes never schedule-fire: empty days array.
        training_schedule: training_mode === 'schedule'
          ? formValues.training_schedule
          : { ...formValues.training_schedule, days: [] },
        promotion_policy: { mode: promotion_mode, demote_previous: true },
        retrain_triggers: {
          on_new_data: {
            enabled: training_mode === 'data',
            min_rows: Number(retrain_min_rows) || 100,
            cooldown_seconds: Number(retrain_cooldown) || DEFAULT_RETRAIN_PACING,
          },
        },
        budget: {
          monthly_credit_cap: monthly_budget ? Number(monthly_budget) : null,
        },
      };
      // Unified models array — the pipeline's model pool. Omitted when
      // no model is selected in the form (foundation-mode pipelines
      // edit name/schedule/automation without touching the models
      // list).
      if (blueprint) {
        payload.models = [
          { model: blueprint },
          ...(challenger_blueprints || []).map((uid) => ({ model: uid })),
        ];
      }

      dispatch(editModelRequest(modelId, payload));
      // We rely on the modelEditing / modelEditError states for the result.
    } catch (err) {
      console.error("Error dispatching edit action:", err);
      setError(err?.message || "Failed to save pipeline changes");
      setIsSubmitting(false);
    }
  }, [formValues, dispatch, modelId]);

  // Effect to handle success/error state changes
  useEffect(() => {
    if (!modelEditing && isSubmitting) {
      if (modelEditError) {
        setError(modelEditError);
        setIsSubmitting(false);
      } else if (modelEditResponse) {
        setSuccess(true);
        setIsSubmitting(false);
        setTimeout(() => {
          window.location.href = `/pipeline/${modelId}`;
        }, 0);
      } else {
        setIsSubmitting(false);
      }
    }
  }, [modelEditing, modelEditError, modelEditResponse, isSubmitting, modelId]);

  // Add cleanup effect
  useEffect(() => {
    return () => {
      dispatch(clearState());
    };
  }, [dispatch]);

  // ---- Shared design primitives (mirrors CreateModelPage) -----------------
  const accent = (key) => theme.palette[key]?.main || theme.palette.primary.main;

  const CardHead = ({ icon, title, helper }) => (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
        <Box sx={{ display: 'flex', color: 'primary.main' }}>{icon}</Box>
        <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
      </Stack>
      {helper && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
          {helper}
        </Typography>
      )}
    </Box>
  );

  const SelectCard = ({ color, icon, title, tagline, description, active, onClick, disabled }) => (
    <Box
      onClick={disabled ? undefined : onClick}
      role="button"
      tabIndex={disabled ? -1 : 0}
      sx={{
        cursor: disabled ? 'default' : 'pointer',
        position: 'relative',
        p: 1.25,
        borderRadius: 2,
        border: `1.5px solid ${active ? color : theme.palette.divider}`,
        bgcolor: active ? alpha(color, 0.1) : 'transparent',
        opacity: disabled ? 0.45 : 1,
        transition: 'all 120ms ease',
        '&:hover': disabled ? {} : { borderColor: color, bgcolor: alpha(color, 0.06) },
      }}
    >
      {active && <CheckCircleIcon sx={{ position: 'absolute', top: 8, right: 8, fontSize: 16, color }} />}
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: tagline || description ? 0.25 : 0, pr: active ? 2.5 : 0 }}>
        {icon && <Box sx={{ display: 'flex', color }}>{icon}</Box>}
        <Typography variant="body2" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>{title}</Typography>
      </Stack>
      {tagline && <Typography variant="caption" sx={{ color, fontWeight: 600, display: 'block' }}>{tagline}</Typography>}
      {description && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.3 }}>
          {description}
        </Typography>
      )}
    </Box>
  );

  const grid3 = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
    gap: 1,
  };

  // One picker drives both stored fields (``model.blueprint`` +
  // ``model.challenger_blueprints``) — together they form the model
  // pool: every entry trains each cycle. They all train on the
  // pipeline's segment, whose labels fix the prediction target, so
  // cycle scores stay directly comparable.
  const renderBlueprintSelect = () => {
    if (blueprintsLoading) {
      return <CircularProgress size={24} />;
    }

    if (!blueprints || blueprints.length === 0) {
      return <Typography color="text.secondary">No models available</Typography>;
    }

    const selectedIds = [
      ...(formValues.blueprint ? [formValues.blueprint] : []),
      ...(formValues.challenger_blueprints || []),
    ];

    const handleBlueprintsChange = (e) => {
      const raw = typeof e.target.value === 'string'
        ? e.target.value.split(',')
        : e.target.value;
      const next = raw.filter(Boolean);
      setFormValues((prev) => ({
        ...prev,
        blueprint: next[0] || '',
        challenger_blueprints: next.slice(1),
      }));
    };

    const nameOf = (uid) => blueprints.find((b) => b.uid === uid)?.name || uid;

    return (
      <Box>
        <FormControl fullWidth size="small">
          <Select
            multiple
            name="blueprint"
            value={selectedIds}
            onChange={handleBlueprintsChange}
            displayEmpty
            renderValue={(selected) => (selected && selected.length
              ? (
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((uid, idx) => (
                    <Chip
                      key={uid}
                      size="small"
                      variant="outlined"
                      label={nameOf(uid)}
                    />
                  ))}
                </Stack>
              )
              : <Typography component="span" color="text.secondary">Select one or more models…</Typography>)}
          >
            {blueprints
              .filter((b) => b.kind !== 'byom')
              .map((blueprint) => (
                <MenuItem key={blueprint.uid} value={blueprint.uid}>
                  {blueprint.name || blueprint.uid}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {selectedIds.length > 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            All {selectedIds.length} train on the same data every run; the best score wins. Each adds one run's cost.
          </Typography>
        )}
      </Box>
    );
  };

  const renderSegmentSelect = () => {
    if (segmentsLoading) {
      return <CircularProgress size={24} />;
    }

    if (!segments || segments.length === 0) {
      return <Typography color="text.secondary">No segments available</Typography>;
    }

    return (
      <FormControl fullWidth size="small">
        <Select
          name="segment"
          value={formValues.segment || ''}
          onChange={handleInputChange}
          required
          displayEmpty
          renderValue={(selected) => (selected
            ? (segments.find((s) => s.uid === selected)?.name || selected)
            : <Typography component="span" color="text.secondary">Select a segment…</Typography>)}
        >
          {segments.map((segment) => (
            <MenuItem key={segment.uid} value={segment.uid}>
              {segment.name || segment.uid}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  if (modelDetailLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const saveDisabled = modelEditing || isSubmitting || !formValues.name
    || !formValues.segment || !formValues.blueprint;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 1.5, sm: 2.5 }, maxWidth: 1500, mx: 'auto' }}>
        {/* Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          spacing={1}
        >
          <PageBreadcrumbs
            items={[
              { text: 'Pipelines', icon: <PipelinesIcon />, link: '/pipelines' },
              { text: modelDetail?.name || modelDetail?.uid || modelId, link: `/pipeline/${modelId}` },
              { text: 'Edit pipeline' },
            ]}
            mb={0}
          />
          <Button
            variant="contained"
            startIcon={(modelEditing || isSubmitting) ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            disabled={saveDisabled}
            onClick={handleSubmit}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {(modelEditing || isSubmitting) ? 'Saving…' : 'Save changes'}
          </Button>
        </Stack>

        {success && (
          <Alert severity="success" sx={{ mt: 1 }}>Pipeline updated successfully! Redirecting…</Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
        )}

        <Box sx={{ mt: 1.5 }}>
          {/* Row 1: source + data */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, alignItems: 'stretch' }}>
            {/* Card: name + models */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Stack spacing={2}>
                <Box>
                  <CardHead icon={<InsightsIcon fontSize="small" />} title="Pipeline name" />
                  <CMDarkTextField
                    name="name"
                    value={formValues.name}
                    onChange={handleInputChange}
                    placeholder="Name this pipeline"
                    required
                  />
                </Box>
                <Box>
                  <CardHead icon={<ModelsIcon fontSize="small" />} title="Models" helper={helpTexts.blueprint} />
                  {renderBlueprintSelect()}
                </Box>
              </Stack>
            </Paper>

            {/* Card: data + forecast */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Stack spacing={2}>
                <Box>
                  <CardHead icon={<SegmentsIcon fontSize="small" />} title="Segment" helper={helpTexts.segment} />
                  {renderSegmentSelect()}
                </Box>
                <Box>
                  <FieldLabel label="Forecast horizon" helpText={helpTexts.forecast_horizon} />
                  <CMDarkTextField
                    name="forecast_horizon"
                    type="number"
                    value={formValues.forecast_horizon}
                    onChange={handleInputChange}
                    inputProps={{ min: 1 }}
                    helperText="Number of future points to forecast"
                  />
                </Box>
              </Stack>
            </Paper>
          </Box>{/* end Row 1 */}

          {/* Live data-sufficiency preview — same component + card as
              CreateModelPage. Watches segment + horizon + model so the
              user sees instantly whether their changes stay trainable. */}
          {(() => {
            const selectedSegment = segments?.find((s) => s.uid === formValues.segment);
            const selectedBlueprint = blueprints?.find((b) => b.uid === formValues.blueprint);
            const seqLen = selectedBlueprint?.training_details?.sequence_length
              || selectedBlueprint?.foundation?.lookback;
            if (!selectedSegment || !seqLen) return null;
            const previewKind = selectedBlueprint?.kind || 'custom';
            return (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 1.5 }}>
                <CardHead icon={<SegmentsIcon fontSize="small" />} title="Data check" helper="Whether your segment has enough rows for this lookback and horizon." />
                <PipelinePreview
                  segment={selectedSegment}
                  sequenceLength={seqLen}
                  forecastHorizon={Number(formValues.forecast_horizon) || 1}
                  dataSplit={selectedBlueprint?.data_split}
                  kind={previewKind}
                />
              </Paper>
            );
          })()}

          {/* Row: Training (trigger + cadence / data knobs) | Automation
              (promotion, budget, active) — side by side, same as create. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
              mt: 1.5,
              alignItems: 'stretch',
            }}
          >
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <CardHead
                icon={<ScheduleIcon fontSize="small" />}
                title="Training"
                helper="What triggers a training run."
              />
              <Box sx={grid3}>
                {[
                  { value: 'once', title: 'Manual only', tagline: 'on demand', description: 'No automatic runs. Train anytime from the pipeline page.', accentKey: 'success' },
                  { value: 'schedule', title: 'On a schedule', tagline: 'recurring', description: 'Retrains on a fixed cadence — hourly, daily, weekly, or custom.', accentKey: 'info' },
                  { value: 'data', title: 'On new data', tagline: 'event-driven', description: 'Retrains when enough new rows land in the segment.', accentKey: 'warning' },
                ].map((opt) => (
                  <SelectCard
                    key={opt.value}
                    color={accent(opt.accentKey)}
                    title={opt.title}
                    tagline={opt.tagline}
                    description={opt.description}
                    active={formValues.training_mode === opt.value}
                    onClick={() => setFormValues((prev) => ({
                      ...prev,
                      training_mode: opt.value,
                      // Entering schedule mode with an empty (run-once shaped)
                      // schedule seeds a sane daily default for the editor.
                      training_schedule: (opt.value === 'schedule'
                        && !(prev.training_schedule?.days || []).length)
                        ? { start_time: '00:00:00', end_time: '23:59:59',
                            days: [...daysOfWeek], frequency: '86400' }
                        : prev.training_schedule,
                    }))}
                  />
                ))}
              </Box>

              {formValues.training_mode === 'schedule' && (
                <Box sx={{ mt: 1.5 }}>
                  <TrainingScheduleField
                    value={formValues.training_schedule}
                    onChange={(next) => setFormValues((prev) => ({ ...prev, training_schedule: next }))}
                    helpText={helpTexts.training_schedule}
                    hideOnce
                    title="Cadence"
                  />
                </Box>
              )}

              {formValues.training_mode === 'data' && (
                <Box sx={{ mt: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    <BoltIcon fontSize="small" color="primary" />
                    <CMDarkTextField
                      name="retrain_min_rows"
                      label="Min new rows"
                      type="number"
                      value={formValues.retrain_min_rows}
                      onChange={handleInputChange}
                      inputProps={{ min: 1 }}
                      sx={{ width: 140 }}
                    />
                    <FormControl size="small" sx={{ width: 170 }}>
                      <Select
                        name="retrain_cooldown"
                        value={formValues.retrain_cooldown}
                        onChange={handleInputChange}
                        renderValue={(selected) => `At most ${
                          (RETRAIN_PACING_OPTIONS.find((o) => o.value === Number(selected))?.label
                            || 'every 30 min').toLowerCase()}`}
                      >
                        {RETRAIN_PACING_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    Retrains whenever this many new rows have landed in the segment — at most once per pacing interval. Budget guardrails still apply.
                  </Typography>
                </Box>
              )}

              {formValues.training_mode === 'once' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  No automatic runs. Train anytime with "Run training" on the pipeline page.
                </Typography>
              )}
            </Paper>

            {/* Automation: promotion policy + budget + active */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <CardHead
                icon={<AutoAwesomeIcon fontSize="small" />}
                title="Automation"
                helper="What happens after each training — promotion and spend guardrails."
              />

              <FieldLabel label="Promotion policy" helpText={helpTexts.promotion_policy} />
              <Box sx={grid3}>
                {[
                  { value: 'auto', title: 'Automatic', tagline: 'hands-off', description: 'When a training beats the serving version, promote it and retire the old one.', accentKey: 'success' },
                  { value: 'manual', title: 'Ask me first', tagline: 'review each', description: 'You get a prompt to promote the better version or keep what\u2019s live.', accentKey: 'info' },
                  { value: 'off', title: 'Off', tagline: 'manual only', description: 'Trainings never suggest promotions. Promote from the trainings list yourself.', accentKey: 'warning' },
                ].map((opt) => (
                  <SelectCard
                    key={opt.value}
                    color={accent(opt.accentKey)}
                    title={opt.title}
                    tagline={opt.tagline}
                    description={opt.description}
                    active={formValues.promotion_mode === opt.value}
                    onClick={() => setFormValues((prev) => ({ ...prev, promotion_mode: opt.value }))}
                  />
                ))}
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Box>
                <FieldLabel label="Monthly budget cap" helpText={helpTexts.budget} />
                <CMDarkTextField
                  name="monthly_budget"
                  type="number"
                  value={formValues.monthly_budget}
                  onChange={handleInputChange}
                  placeholder="No cap"
                  inputProps={{ min: 1 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Credits / month. When reached, automated runs pause until next month — manual runs still work.
                </Typography>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              {/* Active toggle — edit-only control (a new pipeline is
                  always created active). */}
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box sx={{ pr: 2 }}>
                  <Typography variant="body2" fontWeight={500}>Active</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Paused pipelines run no scheduled or data-triggered trainings.
                  </Typography>
                </Box>
                <Switch
                  checked={formValues.active}
                  onChange={handleInputChange}
                  name="active"
                />
              </Stack>
            </Paper>
          </Box>{/* end Training | Automation row */}

          {/* Cost estimator — same card as the create page so edits show
              their projected spend before saving. */}
          <Box sx={{ mt: 1.5 }}>
            <TrainingCostEstimator
              blueprint={blueprints?.find((b) => b.uid === formValues.blueprint)}
              segment={segments?.find((s) => s.uid === formValues.segment)}
              forecastHorizon={formValues.forecast_horizon}
              extraModelCount={formValues.challenger_blueprints?.length || 0}
              trainingSchedule={formValues.training_mode === 'schedule'
                ? formValues.training_schedule
                : { ...formValues.training_schedule, days: [] }}
              balance={balance}
            />
          </Box>

          {/* Bottom actions */}
          <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 1.5 }}>
            <Button variant="outlined" onClick={() => { window.location.href = `/pipeline/${modelId}`; }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={(modelEditing || isSubmitting) ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={saveDisabled}
              onClick={handleSubmit}
            >
              {(modelEditing || isSubmitting) ? 'Saving…' : 'Save changes'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

const mapStateToProps = (state) => ({
  blueprints: state.blueprints_list?.response?.models || [],
  segments: state.segments_list?.response?.segments || [],
  blueprintsLoading: state.blueprints_list?.isLoading || false,
  segmentsLoading: state.segments_list?.isLoading || false,
  modelDetail: state.model_detail?.response || null,
  modelDetailLoading: state.model_detail?.isLoading || false,
  modelEditing: state.model_edit?.isLoading || false,
  modelEditResponse: state.model_edit?.response || null,
  modelEditError: state.model_edit?.error || null,
  // Live credit balance for the cost-estimator footer (optional).
  balance: state.billing_balance?.response || null,
});

const mapDispatchToProps = (dispatch) => ({
  fetchBlueprints: (params) => dispatch(fetchBlueprints(params)),
  fetchSegments: (params) => dispatch(fetchSegments(params)),
  getModelDetail: (modelId) => dispatch(getModelDetail(modelId)),
  loadBalance: () => dispatch(fetchBalance()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditModelPage);
